import dayjs from 'dayjs'
import { toJS } from 'mobx'
import { configManager } from '../../../Managers/Config'
import type { IAppConfig } from '../../../Contracts/Configs/IAppConfig'
import type { BaseFieldStore } from '../Stores/BaseFieldStore'
import { FieldType } from '../Stores/Types/FieldTypeEnum'
import { BaseFieldInputStrategy } from './BaseFieldInputStrategy'

/**
 * Стратегія для date полів
 */
export class DateFieldStrategy extends BaseFieldInputStrategy {
    fieldType: FieldType = FieldType.DATE

    /**
     * Отримує формат дати з конфігу або повертає дефолтний
     */
    protected getDateFormat(): string {
        const appConfig = configManager.get<IAppConfig>('AppConfig')
        return appConfig?.dateDefaultFormat || 'YYYY-MM-DD'
    }

    extractValue(event: unknown, field: BaseFieldStore): unknown {
        const value = super.extractValue(event, field)

        // Якщо це dayjs об'єкт, форматуємо його
        if (dayjs.isDayjs(value)) {
            return value.format()
        }

        // Якщо це масив dayjs (date range)
        if (Array.isArray(value) && value.length === 2) {
            return [value[0].startOf('day'), value[1].startOf('day')]
        }

        return value
    }

    transformForEdit(value: unknown, field: BaseFieldStore): unknown {
        value = toJS(value)

        if (field.isDisableTransform) {
            return value
        }

        // Кастомний input трансформер
        if (typeof field.definition.input === 'function') {
            return field.definition.input(value)
        }

        // Обробка масивів dayjs (для date range)
        if (
            Array.isArray(value) &&
            value.length === 2 &&
            dayjs.isDayjs(value[0]) &&
            dayjs.isDayjs(value[1])
        ) {
            return value
        }

        // Перетворюємо рядок в dayjs
        if (typeof value === 'string' && value.length > 0) {
            return dayjs(value).tz('Europe/Kyiv')
        }

        return value
    }

    transformForView(value: unknown, field: BaseFieldStore): unknown {
        if (field.isDisableTransform) {
            return toJS(value)
        }

        // Кастомний view трансформер
        if (typeof field.definition.view === 'function') {
            return field.definition.view(toJS(value), field.formContext)
        }

        let view = toJS(value)

        // Форматуємо дату для відображення
        if (typeof view === 'string' && view.length > 0) {
            view = dayjs(view).tz('Europe/Kyiv')
            view = view.format(this.getDateFormat())
        }

        return view || '-'
    }
}

/**
 * Стратегія для datetime полів
 */
export class DateTimeFieldStrategy extends DateFieldStrategy {
    fieldType: FieldType = FieldType.DATETIME

    /**
     * Отримує формат дати та часу з конфігу або повертає дефолтний
     */
    protected getDateFormat(): string {
        const appConfig = configManager.get<IAppConfig>('AppConfig')
        return appConfig?.datetimeDefaultFormat || 'YYYY-MM-DD HH:mm'
    }

    transformForView(value: unknown, field: BaseFieldStore): unknown {
        if (field.isDisableTransform) {
            return toJS(value)
        }

        if (typeof field.definition.view === 'function') {
            return field.definition.view(toJS(value), field.formContext)
        }

        let view = toJS(value)

        // Форматуємо дату та час для відображення
        if (typeof view === 'string' && view.length > 0) {
            view = dayjs(view).tz('Europe/Kyiv')
            view = view.format(this.getDateFormat())
        }

        return view || '-'
    }
}
